import { z } from 'zod';

/**
 * Executive Cockpit contract (CLAUDE.md §11.1 `/`, §10 `GET /api/sites/:id/cockpit`).
 *
 * Single source of truth for the cockpit payload — imported by the Worker route, its
 * tests, and the React screen. The endpoint reads D1 aggregates (today's kWh/cost/CO₂e,
 * demand profile, per-area breakdown) and current open alerts; the `/live` WebSocket
 * (see ./../live.ts) streams real-time deltas that the client merges over this snapshot.
 *
 * `illustrative` is true until enough live data has flowed to drive every panel — the UI
 * shows an "Illustrative data" chip while it holds (CLAUDE.md §2).
 */

/** Accent token name (mirrors packages/config Accent) — kept as a string enum to avoid a
 * cross-package import in the shared schema. */
export const CockpitAccentSchema = z.enum(['gold', 'cyan', 'green', 'red', 'purple']);
export type CockpitAccent = z.infer<typeof CockpitAccentSchema>;

/** One KPI tile (CLAUDE.md §11.1: energy, cost, carbon, intensity, balance closure). */
export const KpiSchema = z.object({
  key: z.enum(['energy', 'cost', 'carbon', 'intensity', 'closure']),
  label: z.string(),
  value: z.number(),
  unit: z.string(),
  /** Signed % change vs the comparison period; null when no baseline yet. */
  deltaPct: z.number().nullable(),
  /** Whether an up-move is "good" (savings) or "bad" (consumption) for colouring. */
  upIsGood: z.boolean(),
  accent: CockpitAccentSchema,
});
export type Kpi = z.infer<typeof KpiSchema>;

/** A point on the live-vs-baseline demand profile (MW). Either series may be null (gap). */
export const DemandPointSchema = z.object({
  ts: z.string().datetime(),
  /** Measured demand for this bucket (MW). */
  actual: z.number().nullable(),
  /** 30-day baseline demand for this time-of-day (MW). */
  baseline: z.number().nullable(),
});
export type DemandPoint = z.infer<typeof DemandPointSchema>;

/** One slice of the end-use donut. */
export const EndUseSliceSchema = z.object({
  name: z.string(),
  kwh: z.number(),
  pct: z.number(),
  accent: CockpitAccentSchema,
});
export type EndUseSlice = z.infer<typeof EndUseSliceSchema>;

/** One bar in the site/shaft breakdown. */
export const BreakdownBarSchema = z.object({
  area: z.string(),
  kind: z.enum(['shaft', 'plant', 'surface', 'solar']),
  kwh: z.number(),
});
export type BreakdownBar = z.infer<typeof BreakdownBarSchema>;

/** An open alert as shown in the cockpit's live list. */
export const CockpitAlertSchema = z.object({
  id: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  kind: z.string(),
  message: z.string(),
  raisedAt: z.string().datetime(),
  tag: z.string().nullable(),
});
export type CockpitAlert = z.infer<typeof CockpitAlertSchema>;

export const CockpitResponseSchema = z.object({
  siteId: z.string(),
  generatedAt: z.string().datetime(),
  kpis: z.array(KpiSchema),
  demandProfile: z.array(DemandPointSchema),
  endUseMix: z.array(EndUseSliceSchema),
  siteBreakdown: z.array(BreakdownBarSchema),
  openAlerts: z.array(CockpitAlertSchema),
  /** True until live data fully drives the panels — UI shows the illustrative chip. */
  illustrative: z.boolean(),
});
export type CockpitResponse = z.infer<typeof CockpitResponseSchema>;
